import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { API, LocationDTO, LocationFullDTO, VisitorTrackerDTO } from '../../../services/api/api';
import { UtilService } from '../../../services/util.service';
import { ModalService } from '../../../services/gui/modal/modal.service';
import { NavigationService } from '@ngx-seatback/utils';
import { SearchSelectResult } from '../../../services/gui/modal/modal.model';
import { QueryParamService } from '../../../services/lists/query-params';
import { TranslateService } from '@ngx-translate/core';
import { TrackerCreationComponent } from '../../../components/tracking/tracker-creation/tracker-creation.component';
import { ErrorService } from '../../../services/error.service';
import { InitialToggles, MsOptions, TableWrapperOptions } from '../../../services/global/global-interfaces';
import { PermissionService } from '../../../services/permission.service';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StringTransformationService } from '../../../services/transformation-helper/string-transformation.service';

@Component({
  selector: 'sb-page-tracker',
  templateUrl: './tracker.component.html',
  styleUrls: ['./tracker.component.scss'],
  standalone: false,
})
export class TrackerComponent implements OnInit {
  private readonly stringTransformation = inject(StringTransformationService);

  visitorTracker: VisitorTrackerDTO[];
  loading: boolean;
  debounceTimer: ReturnType<typeof setTimeout>;
  filteredLocations: LocationDTO[];
  queryParamsParsed: boolean;
  initialToggles: InitialToggles[];

  productive?: boolean;
  offline?: boolean;
  disabled?: boolean;
  external?: boolean;

  options: TableWrapperOptions;
  msLocationOptions: MsOptions;
  locations: LocationFullDTO[];

  constructor(
    public readonly api: API,
    public readonly util: UtilService,
    private readonly modalService: ModalService,
    public readonly navService: NavigationService,
    public readonly queryParamService: QueryParamService,
    public readonly translate: TranslateService,
    public readonly errorHandler: ErrorService,
    public readonly permissionService: PermissionService,
    private readonly destroyRef: DestroyRef
  ) {
    this.visitorTracker = [];
    this.loading = true;
    this.debounceTimer = null;
    this.filteredLocations = [];
    this.queryParamsParsed = false;
    this.initialToggles = [
      { id: 1, name: 'disabled', text: this.translate.instant('lang.globals.disabled'), value: -1 },
      { id: 2, name: 'productive', text: this.translate.instant('lang.globals.productive'), value: -1 },
      { id: 3, name: 'online', text: this.translate.instant('lang.globals.online'), value: -1 },
      { id: 4, name: 'external', text: this.translate.instant('lang.globals.external'), value: -1 },
    ];
  }

  ngOnInit(): void {
    this.parseQueryParams().then(() => {
      this.queryParamsParsed = true;
    });
  }

  requestDebounced(options?: TableWrapperOptions) {
    if (options) {
      this.options = options;
    }

    if (this.queryParamsParsed) {
      this.refreshQueryParams();
    }
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.refreshFromServer();
    }, 500);
  }

  refreshFromServer() {
    if (this.options?.pagination) {
      this.loading = true;
      this.api
        .dm_visitor_VisitorTracker_retrieveAll(
          {
            productive: this.productive,
            offline: this.offline !== null ? !this.offline : null,
            external: this.external,
            disabled: this.disabled,
            location_id: this.getPreFilteredLocations() ? this.getPreFilteredLocations().id : null,
            search: this.stringTransformation.encodeSearchQueryParam(this.options?.exactMatch, this.options?.search),
          },
          this.options?.pagination
        )
        .pipe(
          take(1),
          takeUntilDestroyed(this.destroyRef),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: (receivedVisitorTracker: VisitorTrackerDTO[]) => {
            this.visitorTracker = receivedVisitorTracker;
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  openCreateModal() {
    this.modalService
      .showNavigationModal({
        entryComponent: {
          value: TrackerCreationComponent,
          data: {
            location: this.getPreFilteredLocations(),
            editMode: false,
          },
        },
        title: this.translate.instant('nav.network.tracker.createTracker'),
        icon: 'plus',
        // modalOptions: {size: 'sm'}
      })
      .then((e: VisitorTrackerDTO) => {
        if (e) {
          this.navService.navigateToPoint(this.util.RoutingID.trackerDetails, [{ id: 'trackerID', value: e.id }]);
        }
      })
      .catch(() => {});
  }

  editTracker(pTracker: VisitorTrackerDTO) {
    this.modalService
      .showNavigationModal({
        entryComponent: {
          value: TrackerCreationComponent,
          data: {
            tracker: pTracker,
            editMode: true,
          },
        },
        title: this.translate.instant('nav.network.tracker') + ' ' + pTracker.id + ': ' + pTracker.name,
        icon: 'settings',
        modalOptions: { size: 'lg' },
      })
      .then(() => {
        this.requestDebounced();
      })
      .catch(() => {
        this.requestDebounced();
      });
  }

  refreshQueryParams() {
    if (this.filteredLocations && this.filteredLocations.length > 0) {
      this.queryParamService.setQueryParam('locationIds', JSON.stringify(this.util.arrayToIdArray(this.filteredLocations)));
    } else {
      this.queryParamService.clearQueryParam('locationIds');
    }
  }

  parseQueryParams() {
    return new Promise(resolve => {
      this.parseToggleParams();
      this.parseFilterQueryParams().then(() => {
        resolve(true);
      });
    });
  }

  parseToggleParams() {
    for (const e of this.initialToggles) {
      const param = this.queryParamService.getQueryParam(e.name);
      let ok = false;
      if (param) {
        try {
          if (parseInt(param, 10) === -1) {
            e.value = -1;
            ok = true;
          }
        } catch (e) {}
        try {
          if (parseInt(param, 10) === 0) {
            e.value = 0;
            ok = true;
          }
        } catch (e) {}
        try {
          if (parseInt(param, 10) === 1) {
            e.value = 1;
            ok = true;
          }
        } catch (e) {}
        if (!ok) {
          e.value = -1;
        }
      } else {
        e.value = -1;
      }
    }
  }

  toggleChange(evt: any) {
    for (const e of evt) {
      if (e.name === 'online') {
        this.offlineChange(e.value);
      }
      if (e.name === 'productive') {
        this.productiveChange(e.value);
      }
      if (e.name === 'disabled') {
        this.disabledChange(e.value);
      }
      if (e.name === 'external') {
        this.externalChange(e.value);
      }
    }
    this.requestDebounced();
  }

  productiveChange(e: number) {
    switch (e) {
      case null:
        this.productive = null;
        break;
      case -1:
        this.productive = null;
        break;
      case 0:
        this.productive = false;
        break;
      case 1:
        this.productive = true;
        break;
    }
  }

  disabledChange(e: number) {
    switch (e) {
      case null:
        this.disabled = null;
        break;
      case -1:
        this.disabled = null;
        break;
      case 0:
        this.disabled = false;
        break;
      case 1:
        this.disabled = true;
        break;
    }
  }

  externalChange(e: number) {
    switch (e) {
      case null:
        this.external = null;
        break;
      case -1:
        this.external = null;
        break;
      case 0:
        this.external = false;
        break;
      case 1:
        this.external = true;
        break;
    }
  }

  offlineChange(e: number) {
    switch (e) {
      case null:
        this.offline = null;
        break;
      case -1:
        this.offline = null;
        break;
      case 0:
        this.offline = false;
        break;
      case 1:
        this.offline = true;
        break;
    }
  }

  parseFilterQueryParams(): Promise<void> {
    return new Promise(res => {
      const locationsQp = this.queryParamService.getQueryParam('locationIds');

      const locationIds: number[] = [];
      if (locationsQp && locationsQp.length > 0) {
        const parsedLocationIds = JSON.parse(locationsQp);
        if (parsedLocationIds && !parsedLocationIds.length) {
          locationIds.push(parsedLocationIds);
        } else {
          locationIds.push(...parsedLocationIds);
        }
        if (locationIds.length > 0) {
          this.api
            .ss_fm_Location_retrieveAll({ id: locationIds })
            .pipe(takeUntilDestroyed(this.destroyRef), take(1))
            .subscribe({
              next: (locations: LocationFullDTO[]) => {
                this.filteredLocations = locations;
                res();
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
        }
      }
      res();
    });
  }

  deleteTracker(tracker: VisitorTrackerDTO) {
    this.modalService
      .confirm({
        title: this.translate.instant('lang.globals.deleteTracker'),
        message: this.translate.instant('lang.globals.deleteTrackerMessage', { value: tracker.name }),
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then((result: boolean) => {
        if (result) {
          this.api
            .dm_visitor_VisitorTracker_deleteVisitorTracker(tracker.id)
            .pipe(take(1))
            .subscribe({
              next: () => {
                this.requestDebounced();
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
        }
      })
      .catch(() => {});
  }

  selectFilter() {
    const initialLocations = this.filteredLocations;
    this.modalService
      .searchSelect({
        attributeType: 'LOCATION',
        datatype: null,
        initial: initialLocations,
        allowEmpty: true,
        isEmpty: false,
        title: '',
      })
      .then((e: SearchSelectResult<LocationDTO>) => {
        if (!e.cancel) {
          if (e.empty || (!e.empty && !e.values)) {
            this.filteredLocations = null;
          } else if (e.values) {
            this.filteredLocations = e.values;
          }
          this.requestDebounced();
        }
      })
      .catch(() => {});
  }

  getPreFilteredLocations(): LocationDTO {
    return this.filteredLocations?.length > 0 ? this.filteredLocations[0] : null;
  }

  msRequest(type: string, options?: MsOptions) {
    if (type === 'LOCATIONS') {
      if (options) {
        this.msLocationOptions = options;
      }
      if (this.msLocationOptions?.pagination) {
        this.api
          .ss_fm_Location_retrieveAll(
            {
              search: this.stringTransformation.encodeSearchQueryParam(false, this.msLocationOptions?.search),
            },
            this.msLocationOptions?.pagination
          )
          .pipe(take(1), takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: (locs: LocationFullDTO[]) => {
              this.locations = locs;
            },
            error: error => {
              this.errorHandler.errorHandler(error);
            },
          });
      }
    }
  }

  update($event: LocationDTO[], type: string) {
    if (type === 'LOCATIONS') {
      if ($event && $event.length > 0) {
        this.filteredLocations = $event;
      } else {
        this.filteredLocations = null;
      }
    }
    this.refreshFromServer();
  }
}
